import { upperFirst } from "lodash-es";
import { memo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { ActionButtonIntent, TriggerButton } from "widgets";

import { pluralizeWord } from "@/app-utils/string.js";
import type { InputTableQuerySpec } from "@/graphql.ts";
import { deleteParents } from "@/redux/modules/parent.ts";
import { type ITableData, reloadTableRows, toggleSelection } from "@/redux/modules/table-data.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { showConfirmationDialog } from "@/services/ConfirmationService.ts";
import { ENTITY_TYPE } from "@/settings/settings.ts";

interface DeleteButtonProps {
  activeTable: ITableData;
  type: string;
}

/**
 * The delete button allows to delete items
 */
function DeleteButton(props: DeleteButtonProps) {
  const dispatch = useDispatch<ReduxDispatch>();

  const { activeTable } = props;

  const entityType = activeTable.tableQuerySpec?.resultType;
  const selectedIds = activeTable.selectionModel.selectedIds;
  const selectedItemNo = selectedIds.length;

  const disabledTooltip = selectedItemNo === 0 ? `No ${entityType} selected` : null;

  const onDelete = useCallback(async () => {
    // This is currently only implemented for parent deletion
    const tableQuerySpec = activeTable.tableQuerySpec;
    if (tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE_PARENT) {
      return dispatch(deleteParents(selectedIds as number[], props.type))
        .then(() => {
          dispatch(toggleSelection(false, tableQuerySpec));
        })
        .then(() => {
          dispatch(reloadTableRows(tableQuerySpec, true));
        });
    } else {
      return Promise.resolve();
    }
  }, [selectedIds]); // Don't include the tableQuerySpec here, otherwise it creates an infinite loop

  return (
    <TriggerButton
      tooltip={disabledTooltip as string}
      onPress={() =>
        showConfirmationDialog({
          title: `Delete ${upperFirst(pluralizeWord(entityType as string))}`,
          message: `Are you sure you want to delete ${selectedItemNo} ${pluralizeWord(props.type, selectedItemNo)}?`,
          mainButton: {
            label: "Delete",
            onPress: onDelete,
            intent: ActionButtonIntent.Danger,
          },
        })
      }
      isDisabled={!!disabledTooltip}
      icon="delete"
      label="Delete"
      showLabel
    />
  );
}

export default memo(DeleteButton);
